export function wirepasAgoText(tsSec: number | null, t: any): string {
  if (!tsSec) return '—'
  const m = Math.floor((Date.now() / 1000 - tsSec) / 60)
  if (m < 1) return t('components.wirepasAssets.justNow')
  if (m < 60) return t('components.wirepasAssets.minutesAgo', { m })
  const h = Math.floor(m / 60)
  if (h < 24) return t('components.wirepasAssets.hoursAgo', { h, m: m % 60 })
  return t('components.wirepasAssets.daysAgo', { d: Math.floor(h / 24), h: h % 24 })
}

/** wirepasAgoText for an ISO timestamp string instead of raw unix seconds. */
export function wirepasAgoTextFromIso(iso: string | null, t: any): string {
  return wirepasAgoText(iso ? Math.floor(new Date(iso).getTime() / 1000) : null, t)
}

/** Duration in unix seconds → human string (Nd Xh Ymin). Empty for negative deltas. */
export function wirepasDurText(fromSec: number | null, toSec: number = Math.floor(Date.now() / 1000)): string {
  if (!fromSec) return '—'
  const ms = (toSec - fromSec) * 1000
  if (ms < 0) return ''
  const d = Math.floor(ms / 86400e3)
  const h = Math.floor((ms % 86400e3) / 3600e3)
  const m = Math.floor((ms % 3600e3) / 60e3)
  if (d > 0) return `${d}d ${h}h ${m}min`
  if (h > 0) return `${h}h ${m}min`
  return `${m}min`
}

// Matches the badge palette in assets/scss/wirepas-locations-view.scss — keep in sync.
const TYPE_COLOR: Record<string, string> = { hala: '#4870b8', crossdock: '#a9801e', sklep: '#3f8f52' }

export function wirepasTypeColor(type: string | null): string {
  return TYPE_COLOR[type ?? ''] ?? '#8F8F8C'
}

// Current-position indicator color — same green as the app's "ok" status, independent of location type.
export const WIREPAS_CURRENT_COLOR = '#3f8f52'

export function shortGwId(gwId: string | null): string {
  if (!gwId) return '—'
  if (gwId.length <= 12) return gwId
  return gwId.slice(0, 5) + '…' + gwId.slice(-4)
}

// Mirrors WirepasAssetService::FLEET_HEALTH_URGENT_DAYS — keep in sync.
export const WIREPAS_FLEET_HEALTH_URGENT_DAYS = 7

export function isWirepasFleetHealthUrgent(daysToLow: number | null): boolean {
  return daysToLow !== null && daysToLow <= WIREPAS_FLEET_HEALTH_URGENT_DAYS
}

export function wirepasFmtVoltage(mv: number | null): string {
  return mv != null ? `${(mv / 1000).toFixed(2)} V` : '—'
}

export function wirepasFmtTrend(mvPerDay: number | null, t: any): string {
  return mvPerDay != null ? `${mvPerDay} mV/d` : t('components.infrastructure.fleetHealth.noTrend')
}

export function wirepasFmtEta(etaSec: number | null): string {
  return etaSec != null ? new Date(etaSec * 1000).toLocaleDateString('pl-PL') : '—'
}

// Mirrors WirepasAssetService::LOW_BATT_THRESHOLD_MV — keep in sync.
export const WIREPAS_LOW_BATT_THRESHOLD_MV = 2800

export function isWirepasBatteryLow(voltageMv: number): boolean {
  return voltageMv < WIREPAS_LOW_BATT_THRESHOLD_MV
}

export function wirepasStatusFilters(t: any): { v: 'all' | 'ok' | 'low_batt' | 'in_transit' | 'departed', l: string }[] {
  return [
    { v: 'all',        l: t('components.wirepasAssets.filterAll') },
    { v: 'ok',         l: t('components.wirepasAssets.status.ok') },
    { v: 'low_batt',   l: t('components.wirepasAssets.status.low_batt') },
    { v: 'in_transit', l: t('components.wirepasAssets.status.in_transit') },
    { v: 'departed',   l: t('components.wirepasAssets.status.departed') },
  ]
}

// Konsola MQTT (WirepasMqttConsoleGrid) formatting helpers below.

export function wirepasFmtTs(tsMs: number): string {
  return new Date(tsMs).toLocaleString('pl-PL')
}

/** Raw payload bytes, hex-encoded and space-separated for readability (e.g. "0a 87 01"). */
export function wirepasFmtHex(hex: string): string {
  return (hex.match(/../g) || []).join(' ')
}

/** Second/minute-scale duration -- unlike wirepasDurText above, which is day/hour-scale. */
export function wirepasShortDurText(ms: number | null): string {
  if (ms == null) return '—'
  const s = Math.round(ms / 1000)
  if (s < 90) return `${s}s`
  const m = Math.round(s / 60)
  if (m < 90) return `${m}min`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}min`
}
